import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { ArrowUpRight, QrCode, Camera, Wallet, ArrowLeftRight, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import EmptyState from '../../components/EmptyState';
import { AllSettled } from '../../components/illustrations';
import QrSettleSheet from '../../components/QrSettleSheet';
import QrScanSheet from '../../components/QrScanSheet';
import WalletSendSheet from '../../components/WalletSendSheet';
import PairLedgerSheet from '../../components/PairLedgerSheet';
import BalanceChip from '../../components/BalanceChip';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

export default function BalancesTab({ ctx }) {
  const { group, formatCurrency, setSelectedSettlement, setShowSettleModal } = ctx;
  const { user } = useAuth();
  // iter119 — QR scan-to-settle state
  const [qrShow, setQrShow] = useState(null);   // { groupId, groupName, toUserId, toName, amount, note }
  const [qrScanOpen, setQrScanOpen] = useState(false);
  // iter219 — wallet settle
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletSendPrefill, setWalletSendPrefill] = useState(null);
  // iter222 — pair-wise ledger drill-down (inline from settlement suggestion)
  const [pairLedgerTarget, setPairLedgerTarget] = useState(null);  // { id, name }

  const fetchWalletBalance = () => {
    // iter233 — wallet retired: keep balance 0 so "Wallet Pay" never renders.
    setWalletBalance(0);
  };
  useEffect(() => { fetchWalletBalance(); }, []);

  const handleSettled = () => {
    // After a successful QR settlement, give the page a moment then refetch via ctx
    if (ctx.fetchGroup) ctx.fetchGroup();
    if (ctx.fetchFund) ctx.fetchFund();
    fetchWalletBalance();
  };

  return (
    <div data-testid="balances-tab-body" className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold">Settlement Suggestions</h3>
        <Button
          size="sm"
          variant="outline"
          className="rounded-full text-xs"
          onClick={() => setQrScanOpen(true)}
          data-testid="open-qr-scanner-btn"
        >
          <Camera className="w-3.5 h-3.5 mr-1" /> Scan QR
        </Button>
      </div>

      {group?.settlement_suggestions?.length === 0 ? (
        <EmptyState
          illustration={AllSettled}
          title="All settled up!"
          description={(
            <>
              Everyone is square. The Settle tab only shows peer-paid expense settlements —
              if your group only uses Group Fund (everyone tops up to a shared treasury), this
              stays empty by design.{' '}
              <a
                href="#help-all-balances-zero"
                onClick={(e) => {
                  e.preventDefault();
                  window.dispatchEvent(new CustomEvent('open-help', { detail: 'all-balances-zero' }));
                }}
                className="text-primary underline decoration-dotted hover:decoration-solid"
                data-testid="settle-help-link"
              >
                Learn more →
              </a>
            </>
          )}
          tone="success"
          testId="all-settled"
        />
      ) : (
        group?.settlement_suggestions?.map((suggestion, index) => {
          // iter119 — context-aware QR buttons:
          //  - If I'M the receiver (to_user_id == me), offer "Show QR" so the
          //    payer can scan with their phone.
          //  - If I'M the payer (from_user_id == me), offer "Scan QR" so I can
          //    settle on the spot.
          //  - Otherwise just the classic "Settle" (manual entry).
          const meId = user?.id;
          const iAmReceiver = meId && suggestion.to_user === meId;
          const iAmPayer = meId && suggestion.from_user === meId;
          const iAmInPair = iAmReceiver || iAmPayer;
          // The "other" side of the pair for the ledger drill-down
          const otherId = iAmPayer ? suggestion.to_user : suggestion.from_user;
          const otherName = iAmPayer ? suggestion.to_name : suggestion.from_name;
          // iter243 — receiver saved a UPI ID → one-tap pay is available
          const receiverHasUpi = !!group?.members?.find((m) => m.id === suggestion.to_user)?.upi_id;
          return (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: index * 0.05 }}
          >
            <Card
              className="p-4 border-2 rounded-2xl"
              data-testid={`settlement-suggestion-${index}`}
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-negative/20 flex items-center justify-center shrink-0">
                    <ArrowUpRight className="w-5 h-5 text-negative" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{suggestion.from_name}</p>
                    <p className="text-sm text-muted-foreground truncate">
                      pays {suggestion.to_name}
                      {receiverHasUpi && (
                        <span
                          className="inline-flex items-center gap-0.5 ml-1.5 px-1.5 py-px rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-xs font-bold uppercase tracking-wide align-middle"
                          title={`${suggestion.to_name} saved a UPI ID — one-tap pay available`}
                          data-testid={`upi-badge-${index}`}
                        >
                          <Zap className="w-2.5 h-2.5" /> UPI
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <p className="font-display text-lg font-bold amount-display">
                    {formatCurrency(suggestion.amount)}
                  </p>
                  {iAmInPair && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="rounded-full text-xs h-8 px-2 text-muted-foreground hover:text-foreground"
                      onClick={() => setPairLedgerTarget({ id: otherId, name: otherName })}
                      data-testid={`why-btn-${index}`}
                      title={`Show pair-wise ledger between you and ${otherName}`}
                    >
                      <ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Why?
                    </Button>
                  )}
                  {iAmReceiver && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full text-xs"
                      onClick={() => setQrShow({
                        groupId: group.id,
                        groupName: group.name,
                        toUserId: meId,
                        toName: suggestion.to_name,
                        amount: suggestion.amount,
                        note: `Settle: ${suggestion.from_name} → ${suggestion.to_name}`,
                      })}
                      data-testid={`show-qr-btn-${index}`}
                      title="Show this QR for the payer to scan"
                    >
                      <QrCode className="w-3.5 h-3.5 mr-1" /> Show QR
                    </Button>
                  )}
                  {iAmPayer && (() => {
                    // iter217 — If receiver has UPI ID set, offer one-tap UPI intent
                    const receiver = group?.members?.find((m) => m.id === suggestion.to_user);
                    const receiverUpi = receiver?.upi_id;
                    if (!receiverUpi) return null;
                    const upiHref = `upi://pay?pa=${encodeURIComponent(receiverUpi)}` +
                      `&pn=${encodeURIComponent(suggestion.to_name || 'Member')}` +
                      `&am=${suggestion.amount}` +
                      `&cu=INR` +
                      `&tn=${encodeURIComponent(`SplitEasy: ${group?.name || 'group'}`)}`;
                    return (
                      <a
                        href={upiHref}
                        data-testid={`pay-via-upi-btn-${index}`}
                        title={`Open UPI app to pay ₹${suggestion.amount} to ${suggestion.to_name}`}
                        className="inline-flex items-center rounded-full text-xs h-8 px-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold transition-colors"
                      >
                        <span className="mr-1 text-[13px]">💸</span> Pay ₹{Math.round(suggestion.amount)}
                      </a>
                    );
                  })()}
                  {iAmPayer && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full text-xs"
                      onClick={() => setQrScanOpen(true)}
                      data-testid={`scan-qr-btn-${index}`}
                      title="Scan the receiver's QR to settle instantly"
                    >
                      <Camera className="w-3.5 h-3.5 mr-1" /> Scan QR
                    </Button>
                  )}
                  {iAmPayer && walletBalance >= suggestion.amount && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full text-xs bg-primary/10 border-primary/30 hover:bg-primary/20"
                      onClick={() => setWalletSendPrefill({
                        to_user_id: suggestion.to_user,
                        to_name: suggestion.to_name,
                        amount: suggestion.amount,
                        group_id: group.id,
                        group_name: group.name,
                        note: `Settle: ${suggestion.from_name} → ${suggestion.to_name}`,
                      })}
                      data-testid={`pay-via-wallet-btn-${index}`}
                      title={`Pay ₹${Math.round(suggestion.amount)} from your wallet (balance ${formatCurrency(walletBalance)})`}
                    >
                      <Wallet className="w-3.5 h-3.5 mr-1" /> Wallet Pay
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => {
                      setSelectedSettlement(suggestion);
                      setShowSettleModal(true);
                    }}
                    data-testid={`settle-btn-${index}`}
                    className="rounded-full"
                  >
                    Settle
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
          );
        })
      )}

      <h3 className="font-display font-bold mt-6">All Balances</h3>
      <div className="space-y-2">
        {group?.members?.map((member) => {
          const balance = group.balances?.[member.id] || { to_pay: 0, to_receive: 0, net: 0 };
          return (
            <Card
              key={member.id}
              className="p-3 border-2 rounded-xl"
              data-testid={`member-balance-${member.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium truncate">{member.name}</span>
                <BalanceChip amount={balance.net} testId={`member-balance-chip-${member.id}`} />
              </div>
            </Card>
          );
        })}
      </div>

      {/* QR receive sheet */}
      <QrSettleSheet
        open={!!qrShow}
        onOpenChange={(o) => !o && setQrShow(null)}
        groupId={qrShow?.groupId}
        groupName={qrShow?.groupName}
        toUserId={qrShow?.toUserId}
        toName={qrShow?.toName}
        amount={qrShow?.amount}
        note={qrShow?.note}
      />
      {/* QR scan sheet */}
      <QrScanSheet
        open={qrScanOpen}
        onOpenChange={setQrScanOpen}
        onSettled={handleSettled}
      />
      {/* iter219 — Wallet send sheet, prefilled from Settle */}
      <WalletSendSheet
        open={!!walletSendPrefill}
        onOpenChange={(o) => !o && setWalletSendPrefill(null)}
        prefill={walletSendPrefill}
        onSent={handleSettled}
      />
      {/* iter222 — Pair-wise ledger drill-down */}
      <PairLedgerSheet
        open={!!pairLedgerTarget}
        onOpenChange={(o) => !o && setPairLedgerTarget(null)}
        groupId={group?.id}
        otherUserId={pairLedgerTarget?.id}
        otherName={pairLedgerTarget?.name}
      />
    </div>
  );
}
